import { AccredibleCheckboxInputComponentModule } from '@accredible-frontend-v2/forms/checkbox-input';
import {
  AccredibleRadioGroupOption,
  AccredibleRadioInputComponentModule,
} from '@accredible-frontend-v2/forms/radio-input';
import { AccredibleSearchInputComponentModule } from '@accredible-frontend-v2/forms/search-input';
import {
  AccredibleSelectInputComponentModule,
  AccredibleSelectInputOption,
} from '@accredible-frontend-v2/forms/select-input';
import { AccredibleObjectWithId, ActiveFeatureFlags } from '@accredible-frontend-v2/models';
import { AccredibleGeolocationApiService } from '@accredible-frontend-v2/services/geolocation';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import {
  AccredibleLocationApiService,
  DistanceMetric,
  LocationSearchParams,
  LocationSearchPrediction,
} from '@accredible-frontend-v2/services/location';
import { AccredibleRecipientFeatureFlagsService } from '@accredible-frontend-v2/services/recipient-feature-flags';
import { CountriesAndStates } from '@accredible-frontend-v2/utils/countries-states';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { MatLegacyButtonModule as MatButton } from '@angular/material/legacy-button';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { SpotlightProfilesOrder } from '../../../../../../core/models/spotlight-profiles.model';
import { getRadiusInApiFormat } from '../../../../helpers/location-filter.helper';
import { FilterService } from '../../../../services/filter.service';
import {
  SpotlightProfilesState,
  SpotlightProfilesStateAction,
} from '../../../../store/spotlight-profiles/spotlight-profiles.reducer';
import {
  selectSpotlightProfilesFilters,
  selectSpotlightProfilesStateAction,
} from '../../../../store/spotlight-profiles/spotlight-profiles.selectors';

interface LocationFormControl {
  id: string;
  description: string;
}

const TRANSLATION_KEY = 'profile-list-location-filter';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    AccredibleCheckboxInputComponentModule,
    AccredibleSearchInputComponentModule,
    AccredibleRadioInputComponentModule,
    AccredibleSelectInputComponentModule,
    MatSliderModule,
    MatButton,
  ],
  selector: 'sl-profile-list-location-filter',
  templateUrl: './profile-list-location-filter.component.html',
  styleUrls: ['./profile-list-location-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileListLocationFilterComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _locationApi = inject(AccredibleLocationApiService);
  private readonly _filter = inject(FilterService);
  private readonly _spotlightProfileStore = inject(Store<SpotlightProfilesState>);
  private readonly _geolocationApi = inject(AccredibleGeolocationApiService);
  private readonly _destroyRef = inject(DestroyRef);

  isLoading = signal(false);
  locationSearchPredictions = signal<AccredibleObjectWithId[]>([]);
  radiusValue = signal(5);

  readonly locationFormControl = new FormControl<LocationFormControl>(null);
  readonly radiusDistanceFormControl = new FormControl({ value: false, disabled: true });
  readonly radiusMetricFormControl = new FormControl<DistanceMetric>(DistanceMetric.MILES);
  readonly radiusMetricOptions: AccredibleRadioGroupOption[] = [
    {
      label: this._language.translate(`${TRANSLATION_KEY}.miles`),
      value: DistanceMetric.MILES,
    },
    {
      label: this._language.translate(`${TRANSLATION_KEY}.kilometres`),
      value: DistanceMetric.KILOMETRES,
    },
  ];
  readonly MIN_RADIUS_DISTANCE = 5;
  readonly MIN_RADIUS_DISTANCE_MI_TEXT = `${this.MIN_RADIUS_DISTANCE} mi`;
  readonly MIN_RADIUS_DISTANCE_KM_TEXT = `${this.MIN_RADIUS_DISTANCE} km`;
  readonly MAX_RADIUS_DISTANCE_MI = 120;
  readonly MAX_RADIUS_DISTANCE_MI_TEXT = `${this.MAX_RADIUS_DISTANCE_MI} mi`;
  readonly MAX_RADIUS_DISTANCE_KM = 200;
  readonly MAX_RADIUS_DISTANCE_KM_TEXT = `${this.MAX_RADIUS_DISTANCE_KM} km`;
  readonly distanceMetric = DistanceMetric;

  readonly selectCountryTranslation = this._language.translate(`${TRANSLATION_KEY}.select_country`);
  private readonly _selectStateTranslation = this._language.translate(
    `${TRANSLATION_KEY}.select_state`,
  );
  readonly countryFormControl = new FormControl<string>(this.selectCountryTranslation);
  readonly stateFormControl = new FormControl<string>({
    value: this._selectStateTranslation,
    disabled: true,
  });
  countryOptions = signal<AccredibleSelectInputOption[]>([{ name: this.selectCountryTranslation }]);
  stateOptions = signal<AccredibleSelectInputOption[]>([{ name: this._selectStateTranslation }]);

  private _locationSearchParams: LocationSearchParams;

  ngOnInit(): void {
    this._getCurrentUserLocation();
    this._initLocationFormControls();
    this._filtersResetListener();
    this._radiusDistanceHandling();
    this._setupCountryAndStateOptions();
  }

  onSearchLocations(term: string): void {
    this.isLoading.set(true);
    this._locationSearchParams.input = term;

    if (!term) {
      this.locationSearchPredictions.set([]);
    } else {
      this.locationSearchPredictions.set([]);

      this._locationApi.locationSearch(this._locationSearchParams).subscribe({
        next: (locationSearch) => {
          this.isLoading.set(false);
          if (locationSearch.predictions.length > 0) {
            this.locationSearchPredictions.set(
              locationSearch.predictions.map((prediction: LocationSearchPrediction) => {
                return {
                  id: prediction.place_id,
                  ...prediction,
                };
              }),
            );
          }
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
    }
  }

  onSelectOption(locationSearchPrediction: AccredibleObjectWithId): void {
    this.radiusDistanceFormControl.enable();
    this._resetCountryFormControl();

    this._filter.updateRouterQueryParams({
      location: locationSearchPrediction.id,
      city: locationSearchPrediction.description,
      custom_location_distance: null,
      country: null,
      state: null,
    });
  }

  onRemoveLocation(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesFilters)
      .pipe(take(1))
      .subscribe((filters) => {
        this.radiusDistanceFormControl.reset(false, { emitEvent: false });
        this.radiusDistanceFormControl.disable();

        const newParams = { location: null, city: null, custom_location_distance: null };
        if (filters.order === SpotlightProfilesOrder.DISTANCE) {
          newParams['order'] = null;
        }

        this._filter.updateRouterQueryParams(newParams);
      });
  }

  onRadiusDistanceToggle(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesFilters)
      .pipe(take(1))
      .subscribe((filters) => {
        let newParams: unknown;
        if (this.radiusDistanceFormControl.value) {
          this.radiusValue.set(parseInt(filters.custom_location_distance));
          newParams = {
            custom_location_distance: getRadiusInApiFormat(
              this.radiusValue(),
              this.radiusMetricFormControl.value,
            ),
          };
        } else {
          newParams = { custom_location_distance: null };
        }

        this._filter.updateRouterQueryParams(newParams);
      });
  }

  onRadiusChange(): void {
    this._filter.updateRouterQueryParams({
      custom_location_distance: getRadiusInApiFormat(
        this.radiusValue(),
        this.radiusMetricFormControl.value,
      ),
    });
  }

  clearCountryState(): void {
    this._resetCountryFormControl();
    this._filter.updateRouterQueryParams({ location: null, country: null, state: null });
  }

  private _getCurrentUserLocation(): void {
    // Get the users location and init the searchParams
    this._geolocationApi.geolocate().subscribe((geoLocation) => {
      this._locationSearchParams = {
        types: '(cities)',
        input: geoLocation.city,
        latitude: geoLocation.latitude,
        longitude: geoLocation.longitude,
      };
    });
  }

  private _initLocationFormControls(): void {
    // Initialize location form controls with the selected city or country/state
    const { city, country, state } = this._route.snapshot.queryParams;
    if (city) {
      this.locationFormControl.setValue({
        id: 'initial-location',
        description: city,
      });
    } else if (country) {
      this.countryFormControl.setValue(country);

      if (state) {
        this.stateFormControl.setValue(state);
      }
    }
  }

  private _filtersResetListener(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesStateAction)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((action) => action === SpotlightProfilesStateAction.FILTERS_RESET),
      )
      .subscribe(() => {
        this._resetLocationFormControl();
        this._resetCountryFormControl();
      });
  }

  private _radiusDistanceHandling(): void {
    const { queryParamMap } = this._route.snapshot;
    if (queryParamMap.get('city')) {
      this.radiusDistanceFormControl.enable();
    }

    const radiusParam = queryParamMap.get('custom_location_distance');
    if (radiusParam) {
      this.radiusDistanceFormControl.setValue(true);

      const [, radius, metric] = radiusParam.match(/^(\d+)(mi|km)$/i);
      this.radiusMetricFormControl.setValue(<DistanceMetric>metric);
      this.radiusValue.set(Number(radius));
    }

    this.radiusMetricFormControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._filter.updateRouterQueryParams({
          custom_location_distance: getRadiusInApiFormat(
            this.radiusValue(),
            this.radiusMetricFormControl.value,
          ),
        });
      });
  }

  private _setupCountryAndStateOptions(): void {
    this.countryOptions.update((countryOptions) => [
      ...countryOptions,
      ...CountriesAndStates.map((country) => <AccredibleSelectInputOption>{ name: country.name }),
    ]);

    this._handleCountrySelection();
    this._handleStateSelection();
  }

  private _handleCountrySelection(): void {
    this.countryFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((selectedCountry) => selectedCountry !== this.selectCountryTranslation),
        switchMap((selectedCountry) =>
          this._locationApi
            .getCountryStatePlaceId(selectedCountry)
            .pipe(map((placeId) => ({ selectedCountry, placeId }))),
        ),
        withLatestFrom(this._spotlightProfileStore.select(selectSpotlightProfilesFilters)),
      )
      .subscribe(([{ selectedCountry, placeId }, filters]) => {
        this._setStateOptions(selectedCountry);
        this._resetLocationFormControl();

        const newParams = {
          location: placeId,
          country: selectedCountry,
          city: null,
          custom_location_distance: null,
          state: null,
        };
        if (filters.order === SpotlightProfilesOrder.DISTANCE) {
          newParams['order'] = null;
        }

        this._filter.updateRouterQueryParams(newParams);
      });
  }

  private _handleStateSelection(): void {
    const { queryParamMap } = this._route.snapshot;
    const country = queryParamMap.get('country');
    const state = queryParamMap.get('state');
    if (country) {
      this._setStateOptions(country);
      if (state) {
        this.stateFormControl.setValue(state, { emitEvent: false });
      }
    }

    this.stateFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((selectedState) => selectedState !== this._selectStateTranslation),
        switchMap((selectedState) =>
          this._locationApi
            .getCountryStatePlaceId(selectedState)
            .pipe(map((placeId) => ({ selectedState, placeId }))),
        ),
      )
      .subscribe(({ selectedState, placeId }) => {
        this._filter.updateRouterQueryParams({
          location: placeId,
          state: selectedState,
        });
      });
  }

  private _setStateOptions(selectedCountry: string): void {
    const selectedCountryObject = CountriesAndStates.find(
      (country) => country.name === selectedCountry,
    );

    if (selectedCountryObject?.states) {
      this.stateOptions.set([
        { name: this._selectStateTranslation },
        ...selectedCountryObject.states.map(
          (state) =>
            <AccredibleSelectInputOption>{
              name: state.name,
            },
        ),
      ]);
      this._resetStateFormControl();
      this.stateFormControl.enable();
    } else {
      this._resetStateFormControl();
    }
  }

  private _resetLocationFormControl(): void {
    this.locationFormControl.reset(null, { emitEvent: false });
    this.radiusDistanceFormControl.reset(false, { emitEvent: false });
    this.radiusDistanceFormControl.disable();
    this.locationSearchPredictions.set([]);
  }

  private _resetCountryFormControl(): void {
    this.countryFormControl.reset(this.selectCountryTranslation, { emitEvent: false });
    this._resetStateFormControl();
  }

  private _resetStateFormControl(): void {
    this.stateFormControl.reset(this._selectStateTranslation, {
      emitEvent: false,
    });
    this.stateFormControl.disable();
  }
}
